import React, { useState, useEffect, useRef } from "react";

import { color, motion } from "framer-motion";

import { useAppDispatch, useAppSelector, useMediaQuery } from "../../hooks";

import {
  passPreviouseChats,
  selectChat,
  selectDarkmode,
  SelectOpenState,
  setSharedChatId,
} from "../../states/slices/globalReducer";

import { formatDate, useClipboard } from "../../utils-func/functions";

import { AI_PHOTO } from "../../utils-func/image_exports";
import { FaCopy, FaFilePdf, FaStop, FaVolumeUp } from "react-icons/fa";

import { Fade } from "react-awesome-reveal";

import { Tooltip } from "@mui/material";

import toast from "react-hot-toast";
import { useQuery } from "react-query";
import { GetSharedChat } from "../../services/chat/chat.service";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

export function Share() {
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 640px)");
  const open = useAppSelector(SelectOpenState);
  const { copyToClipboard } = useClipboard();
  const location = useLocation();
  const share_token = location.pathname.split("/").pop();
  const dispatch = useAppDispatch();
  const [renderSpeaking, setRenderSpeaking] = useState(false);

  // const chats = useAppSelector(selectChat);

  useEffect(() => {
    if (share_token) {
      dispatch(setSharedChatId(share_token));
    }
  }, [share_token]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data: sharedChat } = useQuery(["share", share_token], () =>
    GetSharedChat(share_token)
  );

  useEffect(() => {
    if (share_token) dispatch(setSharedChatId(share_token));
  }, [share_token]);
  const readTextAloud = (text: string) => {
    if (!text.trim()) {
      toast.error("no text to be read aloud");
      return;
    }
    setRenderSpeaking(true);

    const textuttereance = new SpeechSynthesisUtterance(text);
    textuttereance.lang = "en-US";
    textuttereance.pitch = 1;
    textuttereance.rate = 1;
    textuttereance.volume = 1;

    window.speechSynthesis.speak(textuttereance);
  };

  const handleCopy = async (str: string) => {
    const success = await copyToClipboard(str);
    if (success) {
      toast.success("text copied to clipboard");
    } else {
      toast.error("Failed to copy to clipboard");
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [sharedChat]);

  const darkmode = useAppSelector(selectDarkmode);

  const semanticColors = (isDarkMode: boolean) => ({
    title: isDarkMode
      ? "text-blue-400 font-semibold text-xl"
      : "text-blue-700 font-semibold text-xl",
    subtitle: isDarkMode
      ? "text-purple-400 font-medium text-lg"
      : "text-purple-700 font-medium text-lg",
    caseTitle: isDarkMode
      ? "text-blue-400 font-semibold"
      : "text-blue-700 font-semibold",
    legalPrinciple: isDarkMode ? "text-emerald-400" : "text-emerald-700",
    citation: isDarkMode ? "text-purple-400 italic" : "text-purple-700 italic",
    conclusion: isDarkMode
      ? "text-rose-400 font-medium"
      : "text-rose-700 font-medium",
    warning: isDarkMode ? "text-amber-400" : "text-amber-700",
    emphasis: isDarkMode ? "text-indigo-400" : "text-indigo-700",
    procedural: isDarkMode ? "text-cyan-400" : "text-cyan-700",
    evidence: isDarkMode ? "text-teal-400" : "text-teal-700",
    statute: isDarkMode ? "text-violet-400" : "text-violet-700",
    default: isDarkMode ? "text-gray-100" : "text-gray-900",
  });

  const colors = semanticColors(darkmode);

  const legalPatterns = [
    {
      regex: /^#\s+(.+?)$/gm,
      className: colors.title,
      removeMarker: true,
    },
    {
      regex: /^#{2,}\s+(.+?)$/gm,
      className: colors.subtitle,
      removeMarker: true,
    },
    {
      regex:
        /(?:^|\n)(?:CASE:|IN THE MATTER OF:|SUIT NO:|APPEAL NO:)(.+?)(?:\n|$)/gi,
      className: colors.caseTitle,
    },
    {
      regex:
        /(?:^|\n)(?:PRINCIPLE:|LEGAL PRINCIPLE:|RATIO:|RATIO DECIDENDI:)(.+?)(?:\n|$)/gi,
      className: colors.legalPrinciple,
    },
    {
      regex: /\[([^\]]+)\]|\(([^\)]+)\)/g,
      className: colors.citation,
    },
    {
      regex:
        /(?:^|\n)(?:CONCLUSION:|JUDGMENT:|HOLDING:|ORDER:|DECISION:)(.+?)(?:\n|$)/gi,
      className: colors.conclusion,
    },
    {
      regex:
        /(?:^|\n)(?:WARNING:|IMPORTANT:|NOTE:|CAVEAT:|DISCLAIMER:)(.+?)(?:\n|$)/gi,
      className: colors.warning,
    },
    {
      regex: /(?:^|\n)(?:PROCEDURE:|PROCEEDINGS:|TIMELINE:)(.+?)(?:\n|$)/gi,
      className: colors.procedural,
    },
    {
      regex: /(?:^|\n)(?:EVIDENCE:|EXHIBITS:|PROOF:)(.+?)(?:\n|$)/gi,
      className: colors.evidence,
    },
    {
      regex: /(?:^|\n)(?:STATUTE:|SECTION:|LAW:|ACT:)(.+?)(?:\n|$)/gi,
      className: colors.statute,
    },
    {
      regex: /\*\*(.+?)\*\*/g,
      className: colors.emphasis,
    },
  ];

  const colorizeText = (text: string) => {
    if (!text) return null;

    const lines = text.split(/\n/);
    const processedLines = lines.map((line, index) => {
      if (!line.trim()) {
        return <div key={index} className="h-4" />;
      }

      let processedLine = line;
      let hasMatch = false;
      let className = colors.default;

      for (const {
        regex,
        className: patternClass,
        removeMarker,
      } of legalPatterns) {
        const matches = Array.from(line.matchAll(new RegExp(regex)));
        if (matches.length > 0) {
          hasMatch = true;
          matches.forEach((match) => {
            const content = match[1] || match[0];
            // Remove the markdown markers if removeMarker is true
            processedLine = removeMarker
              ? content
              : processedLine.replace(
                  match[0],
                  `<span class="${patternClass}">${content}</span>`
                );
          });
          className = patternClass;
        }
      }

      // Handle markdown-style formatting within colored text
      processedLine = processedLine
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.*?)\*/g, "<em>$1</em>")
        .replace(/`(.*?)`/g, "<code>$1</code>")
        .replace(/~~(.*?)~~/g, "<del>$1</del>");

      return (
        <div
          key={index}
          className={`${hasMatch ? "" : className} leading-relaxed py-1`}
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      );
    });

    return <div className="space-y-1">{processedLines}</div>;
  };

  return (
    <div
      className={`w-full pt-8 lg:px-56 px-7 h-screen ${
        darkmode ? "bg-[#141718]" : "bg-[#fbfcfe] "
      }`}
    >
      <div
        ref={chatContainerRef}
        className={`border-t ${
          darkmode ? "border-[#232627]" : ""
        } lg:pt-7 pt-16 h-[500px] pb-16 overflow-y-auto`}
      >
        <div className="flex items-center justify-between">
          <span
            className={`text-[18px] font-semibold  ${
              darkmode ? "text-gray-300" : "text-black"
            }`}
          >
            {"JudexAI (Judge)"}
          </span>
        </div>
        {sharedChat?.messages?.length > 0 &&
          sharedChat?.messages?.map((mess: any) => {
            return (
              <div className="flex flex-col gap-y-10 mt-8" key={mess?.id}>
                {mess?.sender === "user" ? (
                  <Fade
                    direction="up"
                    duration={500}
                    className="w-full flex flex-col items-end justify-end gap-y-2"
                  >
                    <div className="flex items-end flex-col gap-y-2">
                      {/* Display attached files */}
                      {mess?.file_path?.length > 0 &&
                        mess?.file_path.map((file: any) => {
                          const isImage = /\.(jpg|jpeg|png|gif)$/i.test(file);
                          return isImage ? (
                            <div className="lg:w-[200px] w-[75px] h-[75px] rounded-lg lg:h-[200px] -mb-7">
                              <img
                                src={file}
                                className="w-full h-full rounded-2xl object-contain"
                                alt="Uploaded File"
                              />
                            </div>
                          ) : (
                            <FaFilePdf size={isMobile ? 75 : 150} color="red" />
                          );
                        })}
                      {/* User message bubble */}
                      <div
                        className={`px-4 py-3 ${
                          darkmode
                            ? "border-0 bg-[#232627]"
                            : "border border-[#E8ECEF] bg-white"
                        } rounded-2xl lg:max-w-md md:max-w-lg sm:max-w-sm max-w-full`}
                      >
                        <span
                          className={`lg:text-base md:text-sm text-xs font-semibold ${
                            darkmode ? "text-white/80" : "text-[#6E6E6E]"
                          } break-words`}
                        >
                          {mess?.content}
                        </span>
                      </div>
                      <span className="lg:text-sm text-xs font-normal text-[#C9C9C9]">
                        {formatDate(mess?.created_at)}
                      </span>
                    </div>
                  </Fade>
                ) : (
                  <div className="w-full flex gap-y-2 items-start gap-x-2">
                    <img
                      src={AI_PHOTO}
                      className="lg:w-[30px] lg:h-[30px] w-[25px] h-[25px]"
                      alt="AI"
                    />
                    <div
                      className="flex flex-col items-start gap-y-1 -mt-3"
                      onMouseEnter={() => setHoveredMessage(mess?.id)}
                      onMouseLeave={() => setHoveredMessage(null)}
                    >
                      <div className="px-4 py-2 rounded-xl lg:max-w-full md:max-w-lg sm:max-w-sm max-w-full">
                        <motion.div
                          initial={{
                            opacity: 0,
                            clipPath: "inset(0% 0% 100% 0%)",
                          }}
                          animate={{
                            opacity: 1,
                            clipPath: "inset(0% 0% 0% 0%)",
                          }}
                          transition={{ duration: 1.5, ease: "easeOut" }} // Adjust speed as needed
                          className="lg:text-base md:text-sm text-xs font-normal overflow-hidden text-[#6E6E6E] break-words"
                        >
                          {colorizeText(mess?.content)}
                        </motion.div>
                      </div>
                      {/* Message Actions */}
                      <div
                        className={`flex items-center gap-x-2 ml-3 ${
                          hoveredMessage === mess?.id
                            ? "opacity-100"
                            : "opacity-0"
                        } text-white/30`}
                      >
                        {renderSpeaking ? (
                          <Tooltip title="Stop">
                            <span
                              className={`flex items-center justify-center ${
                                darkmode
                                  ? "hover:bg-white/10"
                                  : "text-gray-500 hover:bg-gray-200"
                              } w-[30px] h-[30px] rounded-md`}
                              onClick={() => {
                                window.speechSynthesis.cancel();
                                setRenderSpeaking(false);
                              }}
                            >
                              <FaStop className="cursor-pointer" />
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Read aloud">
                            <span
                              className={`flex items-center justify-center ${
                                darkmode
                                  ? "hover:bg-white/10"
                                  : "text-gray-500 hover:bg-gray-200"
                              } w-[30px] h-[30px] rounded-md`}
                              onClick={() => readTextAloud(mess?.content)}
                            >
                              <FaVolumeUp className="cursor-pointer" />
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Copy">
                          <span
                            className={`flex items-center justify-center ${
                              darkmode
                                ? "hover:bg-white/10"
                                : "text-gray-500 hover:bg-gray-200"
                            } w-[30px] h-[30px] rounded-md`}
                            onClick={() => handleCopy(mess?.content)}
                          >
                            <FaCopy className="cursor-pointer" />
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        <div ref={messagesEndRef} />
        <div
          className={`lg:w-[65%] w-[85%] h-[130px] bottom-7 rounded-xl fixed flex flex-col justify-center items-center space-y-4 p-4 ${
            darkmode ? "bg-[#1f2224] text-white" : "bg-[#e6eaf3] text-black"
          }`}
        >
          <p className="text-center text-sm">
            Log in to access chat features and explore our AI-powered tool.
          </p>

          <div className="flex space-x-4">
            <button
              onClick={() => navigate("/sign-in")}
              className={`px-6 py-2 rounded-lg font-medium transition ${
                darkmode
                  ? "bg-[#3b3f42] text-white hover:bg-[#575b5e]"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              Login
            </button>

            <button
              onClick={() => navigate("/sign-up")}
              className={`px-6 py-2 rounded-lg font-medium transition ${
                darkmode
                  ? "bg-[#2545D3] text-white hover:bg-[#1d3aa8]"
                  : "bg-[#002A7F] text-white hover:bg-[#001f5c]"
              }`}
            >
              Sign Up
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
